import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AGENT_TEST_LOGIN_BODY_SCHEMA } from "shared/AgentTestLogin";
import { postAgentTestSignIn } from "src/CalmLensApi";
import Button from "src/components/common/Button/Button";
import Callout from "src/components/common/Callout/Callout";
import Typography from "src/components/common/Typography/Typography";
import Form from "src/components/form/Form/Form";
import FormLayout from "src/components/form/FormLayout/FormLayout";
import FormSection from "src/components/form/FormSection/FormSection";
import TextField from "src/components/form/TextField/TextField";
import { loadFirebaseApp, useFirebaseSignInWithCredential } from "src/lib/FirebaseUtils";
import { metadataHead } from "src/lib/Metadata";
import { useAppRouter } from "src/lib/Navigation";
import { useExtracted } from "#i18n";

export const Route = createFileRoute("/agent-login")({
  head: () =>
    metadataHead({ title: "Agent test sign-in", robots: { index: false, follow: false } }),
  component: AgentLogin,
});

function AgentLogin() {
  const t = useExtracted();
  const router = useAppRouter();
  const signIn = useFirebaseSignInWithCredential();
  const [ready, setReady] = useState(false);
  useEffect(() => setReady(true), []);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  return (
    <main className="mx-auto w-full max-w-form px-6 py-12">
      <Typography as="h1" variant="heading1">
        {t("Agent test sign-in")}
      </Typography>
      <Form
        method="post"
        validationSchema={AGENT_TEST_LOGIN_BODY_SCHEMA}
        disabled={pending || !ready}
        onSubmit={async (values) => {
          setPending(true);
          setError(false);
          try {
            const { email, password } = AGENT_TEST_LOGIN_BODY_SCHEMA.parse(values);
            const { data } = await postAgentTestSignIn.submit({ body: { email, password } });
            const { getAuth, signInWithCustomToken } = await import("firebase/auth");
            const result = await signInWithCustomToken(
              getAuth(await loadFirebaseApp()),
              data.customToken,
            );
            await signIn(result.user);
            await router.replace("/portal");
          } catch {
            setError(true);
          } finally {
            setPending(false);
          }
        }}
      >
        <FormLayout>
          <FormSection>
            <TextField
              name="email"
              type="email"
              label={t("Email")}
              required
              autoComplete="username"
            />
            <TextField
              name="password"
              type="password"
              label={t("Temporary password")}
              required
              autoComplete="off"
            />
            {error ? (
              <Callout color="danger" role="alert">
                {t("Sign-in failed. Check the temporary credential and try again.")}
              </Callout>
            ) : null}
          </FormSection>
          <FormLayout.Actions>
            <Button
              type="submit"
              label={t("Sign in")}
              loading={pending}
              disabled={!ready || pending}
            />
          </FormLayout.Actions>
        </FormLayout>
      </Form>
    </main>
  );
}
